import { useEffect, useMemo, useState } from "react";
import {
  Check,
  ChevronDown,
  ChevronUp,
  CircleStop,
  Clock3,
  Coins,
  ListTree,
  ShieldCheck,
  X,
} from "lucide-react";
import type { RunEvent, RunStatus } from "../../shared/types";

interface RunPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  runId: string | null;
  status: RunStatus | null;
  events: RunEvent[];
  startedAt: number | null;
  approvalNodeId: string | null;
  onApprove: () => void;
  onReject: () => void;
  onCancel: () => void;
}

const statusLabels: Record<RunStatus, string> = {
  queued: "Na fila",
  running: "Executando",
  waiting_approval: "Aguardando aprovação",
  completed: "Concluída",
  failed: "Falhou",
  cancelled: "Cancelada",
};

function formatDuration(milliseconds: number) {
  const seconds = Math.max(0, Math.floor(milliseconds / 1000));
  const minutes = Math.floor(seconds / 60);
  return `${String(minutes).padStart(2, "0")}:${String(seconds % 60).padStart(2, "0")}`;
}

function eventLabel(event: RunEvent) {
  const label = typeof event.payload.label === "string" ? event.payload.label : null;
  if (event.type === "node.running") return `${label ?? "Nó"} iniciou`;
  if (event.type === "node.completed") return `${label ?? "Nó"} concluiu`;
  if (event.type === "node.error") return `${label ?? "Nó"} falhou`;
  if (event.type === "approval.required") return "Aprovação humana solicitada";
  if (event.type === "approval.approved") return "Etapa aprovada";
  if (event.type === "approval.rejected") return "Etapa rejeitada";
  if (event.type === "run.started") return "Execução iniciada";
  if (event.type === "run.completed") {
    const warningCount = typeof event.payload.warningCount === "number" ? event.payload.warningCount : 0;
    return warningCount > 0 ? `Execução concluída com ${warningCount} alerta(s)` : "Execução concluída";
  }
  if (event.type === "run.failed") return "Execução interrompida por erro";
  return event.type;
}

export function RunPanel({
  open,
  onOpenChange,
  runId,
  status,
  events,
  startedAt,
  approvalNodeId,
  onApprove,
  onReject,
  onCancel,
}: RunPanelProps) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!startedAt || !status || ["completed", "failed", "cancelled"].includes(status)) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [startedAt, status]);

  const totalCost = useMemo(
    () => events.reduce((sum, event) => sum + (typeof event.payload.cost === "number" ? event.payload.cost : 0), 0),
    [events],
  );
  const completedCount = events.filter((event) => event.type === "node.completed").length;
  const running = status === "running" || status === "queued";

  return (
    <section className={`run-panel ${open ? "is-open" : ""}`} aria-label="Execução atual">
      <button className="run-panel__toggle" onClick={() => onOpenChange(!open)} aria-label={open ? "Recolher logs" : "Abrir logs"}>
        {open ? <ChevronDown size={15} /> : <ChevronUp size={15} />}
      </button>
      <div className="run-panel__summary">
        <span className={`run-state run-state--${status ?? "idle"}`} />
        <strong>{status ? statusLabels[status] : "Nenhuma execução ativa"}</strong>
        {runId && <code>#{runId.slice(0, 8)}</code>}
        <span><Clock3 size={13} />{startedAt ? formatDuration(now - startedAt) : "00:00"}</span>
        <span><Coins size={13} />R$ {totalCost.toFixed(3).replace(".", ",")}</span>
        <span><ListTree size={13} />{completedCount} concluídos</span>
      </div>
      <div className="run-panel__commands">
        {running && <button className="icon-button" title="Parar execução" aria-label="Parar execução" onClick={onCancel}><CircleStop size={16} /></button>}
        <button className="icon-button" title="Fechar logs" aria-label="Fechar logs" onClick={() => onOpenChange(false)}><X size={16} /></button>
      </div>

      {open && (
        <div className="run-panel__body">
          <div className="event-stream">
            <div className="event-stream__heading"><span>Tempo</span><span>Evento</span><span>Nó</span></div>
            {events.map((event) => (
              <div className={`event-row event-row--${event.type.includes("error") || event.type.includes("failed") ? "error" : "normal"}`} key={event.id}>
                <time>{new Date(event.createdAt).toLocaleTimeString("pt-BR")}</time>
                <span>{eventLabel(event)}</span>
                <code>{event.nodeId ?? "run"}</code>
              </div>
            ))}
            {events.length === 0 && <div className="event-empty">Inicie um teste para acompanhar os eventos.</div>}
          </div>
          <aside className="run-decision">
            {approvalNodeId ? (
              <>
                <span className="decision-icon"><ShieldCheck size={20} /></span>
                <strong>Decisão necessária</strong>
                <p>Revise os eventos e decida se o fluxo pode continuar.</p>
                <button className="button button--primary" onClick={onApprove}><Check size={15} />Aprovar e continuar</button>
                <button className="button button--danger-ghost" onClick={onReject}><X size={15} />Rejeitar</button>
              </>
            ) : (
              <>
                <span className="decision-icon decision-icon--neutral"><ListTree size={20} /></span>
                <strong>Execução observável</strong>
                <p>Cada decisão e artefato fica registrado no histórico.</p>
              </>
            )}
          </aside>
        </div>
      )}
    </section>
  );
}
